import { Input, Directive, inject, ViewContainerRef, TemplateRef } from '@angular/core';

import { provideMailgunBlockDepth } from '../mailgun/tokens';
import type { WrappedValue } from '../mailgun/wrap-value';
import { wrapValue } from '../mailgun/wrap-value';
import { TemplateRendererConfig } from '../providers/template-renderer-config';
import type { TemplateContext } from './email-mailgun-each.component';
import { EmailMailgunEachComponent } from './email-mailgun-each.component';

@Directive({
  selector: '[mailgunFor]',
  standalone: true,
  providers: [provideMailgunBlockDepth()],
})
export class EmailMailgunEachDirective {
  public config = inject(TemplateRendererConfig);
  public vcRef = inject(ViewContainerRef);
  public template = inject(TemplateRef<any>);

  @Input('mailgunForOf') public items: WrappedValue<any[]>;

  private component = this.vcRef.createComponent(EmailMailgunEachComponent);

  public ngOnChanges() {
    this.displayTemplate();
  }

  private displayTemplate() {
    this.vcRef.clear();

    if (this.config.interpolate) {
      this.displayInterpolatedTemplate();
    } else {
      this.displayMailgunTemplate();
    }
  }

  private displayInterpolatedTemplate() {
    if (Array.isArray(this.items.toValue())) {
      this.items
        .toValue()
        .forEach((_: any, index: number) =>
          this.vcRef.createEmbeddedView(
            this.component.instance.interpolateEach,
            this.getInterpolatedContext(this.items[index], index),
            { index: index },
          ),
        );
    }
  }

  private displayMailgunTemplate() {
    this.vcRef.createEmbeddedView(this.component.instance.mailgunEach, {
      $implicit: wrapValue('', { ...this.items[0].toValue() }, this.config),
      prop: wrapValue('', { ...this.items[0].toValue() }, this.config),
      ownPath: this.items.toPath(),
      template: this.template,
      index: wrapValue('index', 0, this.config),
      first: wrapValue('@first', false, this.config),
      last: wrapValue('@last', false, this.config),
    });
  }

  private getInterpolatedContext(item: any, index: number): TemplateContext {
    const count = Array.isArray(this.items.toValue()) ? this.items.toValue().length : -1;
    const isFirst = index === 0;
    const isLast = index + 1 === count;

    return {
      $implicit: item,
      prop: this.items,
      template: this.template,
      index: wrapValue('index', index, this.config),
      first: wrapValue('@first', isFirst, this.config),
      last: wrapValue('@last', isLast, this.config),
    };
  }
}
